import { Model, Query, Relation } from '@nozbe/watermelondb';

import { ValidationOperation } from '../db/validation/validator';

import { ContactModel } from './Contacts';
import { EntryModel, EntryRawRecord } from './Entries';
import { HorseModel, HorseRawRecord } from './Horses';
import { Identificator } from './Identificator';
import { Location, LocationModel, LocationRawRecord } from './Location';
import { UserModel, UserRawRecord } from './User';
import {
    EVENT_CONTACT_STATUS,
    EventContactModel,
    EventContactRawRecord,
} from './EventContact';
import { ImageService } from './imageService';
import { DBServiceLogger } from './logger';
import { DatabaseValidation } from './validation';
import {
    InventoryChangeModel,
    InventoryChangeRawRecord,
} from './InventoryChange';
import { EntryProcedureModel, EntryProcedureRawRecord } from './EntryProcedure';
import { EntryProductModel, EntryProductRawRecord } from './EntryProduct';
import { EntryUserModel, EntryUserRawRecord } from './EntryUser';
import {
    EventContactReminderModel,
    EventContactReminderRawRecord,
} from './EventContactReminder';
import { EventHorseModel, EventHorseRawRecord } from './EventHorse';
import { EventUserModel, EventUserRawRecord } from './EventUser';
import { HorseContactModel, HorseContactRawRecord } from './HorseContact';
import {
    InventoryProductModel,
    InventoryProductRawRecord,
} from './InventoryProduct';
import {
    OptionalString,
    OptionalBoolean,
    OptionalUuid,
    RequiredUuid,
    UpdateRawRecord,
} from './rawRecordFields';

export enum EventCategory {
    service = 'service',
    standard = 'standard',
}

export type InvitationStatus = 'not_sent' | 'sending' | 'sent';

export type SentSmsStatus =
    | 'awaiting_opt_in'
    | 'suppressed_during_opt_out'
    | 'canceled_opt_out'
    | 'skipped'
    | 'sent_to_provider'
    | 'accepted'
    | 'scheduled'
    | 'queued'
    | 'sending'
    | 'sent'
    | 'delivered'
    | 'read'
    | 'undelivered'
    | 'failed'
    | 'delivery_unknown';

export type SmsStatusTone =
    | 'neutral'
    | 'info'
    | 'success'
    | 'warning'
    | 'error';

export type SmsStatusDisplay = {
    labelKey: string;
    helperKey: string;
    tone: SmsStatusTone;
};

export type ContactInvitation = {
    sms: boolean;
    email: boolean;
    smsAt: string | null;
    emailAt: string | null;
    smsStatus: InvitationStatus;
    emailStatus: InvitationStatus;
    smsDeliveryStatus?: string | null;
};

export type ContactInvitations = {
    [contactId: string]: ContactInvitation;
};

export type ContactRSVPStatus = {
    [contactId: string]: EVENT_CONTACT_STATUS;
};

export type EventModel = {
    address: string | null;
    contacts: Query<ContactModel>;
    sendInvitesToContactsAt: string;
    contactsReminder: boolean;
    createdAt: string;
    endsTime: string;
    entries: Query<EntryModel>;
    horses: Query<HorseModel>;
    id: string;
    locationId?: string | null;
    notes: string | null;
    organisationId: string;
    ownersReminder: boolean;
    startsTime: string;
    title?: string | null;
    updatedAt: string;
    userId: string;
    userReminder?: string | null;
    location: Relation<LocationModel>;
    timezone?: string;
    users: Query<UserModel>;
    category: EventCategory;
    isRecurring: boolean;
    recurrencePattern?: string;
    recurrenceEndDate?: string;
    originalEventId?: string;
} & Model;

export type CalendarEvent = {
    start: Date;
    end: Date;
    color: string;
    eventModel: EventModel;
};

export type ContactInvitationsPayload = {
    [contactId: string]: {
        sms: boolean;
        email: boolean;
    };
};

export type EventPayload = {
    address?: string | null;
    category?: EventCategory;
    contacts?: Identificator[];
    contactsReminder: boolean;
    contactsReminderTimeBefore?: string;
    endsTime: string;
    horses?: Identificator[];
    newHorses?: Identificator[];
    location?: Location | null;
    notes?: string | null;
    ownersReminder: boolean;
    startsTime: string;
    title?: string | null;
    userReminder?: string | null;
    membersIds?: string[];
    createEntries?: boolean;
    timezone?: string;
    invitations?: ContactInvitationsPayload;
    isRecurring?: boolean;
    recurrencePattern?: string;
    recurrenceEndDate?: string;
    originalEventId?: string;
    updateUserTimezone?: boolean;
    updateHorseShoeingCycle?: boolean;
    addNewHorsesToContact?: boolean;
};

export type CalculateEventStatusParams = {
    database: any;
    imageService: ImageService;
    logger: DBServiceLogger;
    eventId: string;
    validation: DatabaseValidation;
};

export type RSVPEventStatus = {
    contactsCount: number;
    contactToShow: ContactModel | null;
    confirmedNumber?: number;
};

export type CalculateEventStatusResult = {
    awaiting: number;
    confirmed: RSVPEventStatus | null;
    declined: RSVPEventStatus | null;
};

export type EventRawRecord = {
    id: string;
    user_id: RequiredUuid;
    organisation_id: RequiredUuid;
    location_id?: OptionalUuid;
    title?: OptionalString;
    address?: OptionalString;
    starts_time: string;
    ends_time: string;
    notes?: OptionalString;
    user_reminder?: OptionalString;
    send_reminders_to_contacts?: OptionalBoolean;
    send_reminders_to_owners?: OptionalBoolean;
    send_invites_to_contacts?: OptionalBoolean;
    send_invites_to_contacts_at?: OptionalString;
    send_invites_to_owners?: OptionalBoolean;
    timezone?: OptionalString;
    category?: OptionalString;
    is_recurring?: OptionalBoolean;
    recurrence_pattern?: OptionalString;
    recurrence_end_date?: OptionalString;
    original_event_id?: OptionalUuid;
    created_at: number;
    updated_at: number;
    _status?: OptionalString;
    _changed?: OptionalString;
};

export type EventUpdateRawRecord = UpdateRawRecord<EventRawRecord>;
export type EventEntityTableName =
    | 'events'
    | 'locations'
    | 'horses'
    | 'entries'
    | 'entry_products'
    | 'entry_procedures'
    | 'entry_users'
    | 'event_contacts'
    | 'event_contact_reminders'
    | 'event_users'
    | 'event_horses'
    | 'horse_contacts'
    | 'inventory_products'
    | 'inventory_changes'
    | 'users';

export type RecordByEventEntityTable = {
    events: EventRawRecord;
    locations: LocationRawRecord;
    horses: HorseRawRecord;
    entries: EntryRawRecord;
    entry_products: EntryProductRawRecord;
    entry_procedures: EntryProcedureRawRecord;
    entry_users: EntryUserRawRecord;
    event_contacts: EventContactRawRecord;
    event_contact_reminders: EventContactReminderRawRecord;
    event_users: EventUserRawRecord;
    event_horses: EventHorseRawRecord;
    horse_contacts: HorseContactRawRecord;
    inventory_products: InventoryProductRawRecord;
    inventory_changes: InventoryChangeRawRecord;
    users: UserRawRecord;
};

export type ModelByEventEntityTable = {
    events: EventModel;
    locations: LocationModel;
    horses: HorseModel;
    entries: EntryModel;
    entry_products: EntryProductModel;
    entry_procedures: EntryProcedureModel;
    entry_users: EntryUserModel;
    event_contacts: EventContactModel;
    event_contact_reminders: EventContactReminderModel;
    event_users: EventUserModel;
    event_horses: EventHorseModel;
    horse_contacts: HorseContactModel;
    inventory_products: InventoryProductModel;
    inventory_changes: InventoryChangeModel;
    users: UserModel;
};

export type PreparedDataToValidate = {
    [K in EventEntityTableName]: {
        table: K;
        record: RecordByEventEntityTable[K];
    };
}[EventEntityTableName];

export type PreparedDataOnAdd = {
    [K in EventEntityTableName]: {
        table: K;
        record: ModelByEventEntityTable[K];
        operation: ValidationOperation;
    };
}[EventEntityTableName];

export type PreparedDataOnUpdate = PreparedDataOnAdd;

export type PreparedDataOnDelete = PreparedDataOnAdd;
